import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
} from "react";
import { AuthService } from "../service/auth_service";
import {
	clearSession,
	endSession,
	isSessionValid,
	reconcileUnidadeSelecionada,
} from "../utils/session";

const UserContext = createContext(null);

/**
 * Mantém o perfil do usuário (nome, unidades, ids) em memória e é a fonte de
 * verdade sobre estar ou não autenticado.
 *
 * O perfil NÃO é persistido em localStorage para evitar deixar dados
 * pessoais (nome, CPF/CNPJ das unidades) em disco. Ele é buscado do
 * servidor a cada carga via getProfile(), garantindo dados sempre atuais.
 *
 * Apenas identificadores não sensíveis (access_token, selected_unidade_id,
 * selected_empresa_id) continuam no localStorage, pois são lidos por código
 * fora do React (repositories) e pelos guards de rota.
 *
 * `authStatus` assume três valores:
 *   - "checking": token local válido, aguardando a confirmação do servidor
 *   - "authenticated": perfil carregado (ou servidor inacessível com token
 *     ainda dentro da validade, para uma falha de rede passageira não
 *     derrubar quem está logado)
 *   - "unauthenticated": sem token, token expirado ou 401 do servidor
 */
export function UserProvider({ children }) {
	const [user, setUser] = useState(null);
	const [authStatus, setAuthStatus] = useState(() =>
		isSessionValid() ? "checking" : "unauthenticated"
	);
	const [loading, setLoading] = useState(() => isSessionValid());

	const refreshUser = useCallback(async () => {
		if (!isSessionValid()) {
			// Token ausente ou expirado: derruba a sessão.
			if (localStorage.getItem("access_token")) {
				await endSession();
			} else {
				clearSession();
			}
			setUser(null);
			setAuthStatus("unauthenticated");
			setLoading(false);
			return null;
		}

		setLoading(true);
		try {
			const profile = await new AuthService().getProfile();
			// Antes de liberar as telas: a unidade guardada precisa ser uma das
			// que este perfil tem. Id de outra sessão vira 403 em toda chamada.
			reconcileUnidadeSelecionada(profile);
			setUser(profile);
			setAuthStatus("authenticated");
			return profile;
		} catch (error) {
			if (error?.status === 401) {
				// Token recusado pelo servidor: sessão morta, volta pro login.
				await endSession();
				setUser(null);
				setAuthStatus("unauthenticated");
				return null;
			}

			// Falha de rede/servidor: o token local continua válido, então
			// mantemos a sessão sem o perfil e tentamos de novo na próxima carga.
			console.error("Erro ao carregar perfil do usuário:", error);
			setAuthStatus(isSessionValid() ? "authenticated" : "unauthenticated");
			return null;
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		refreshUser();
	}, [refreshUser]);

	return (
		<UserContext.Provider
			value={{
				user,
				loading,
				authStatus,
				isAuthenticated: authStatus === "authenticated",
				refreshUser,
				setUser,
			}}
		>
			{children}
		</UserContext.Provider>
	);
}

export function useUser() {
	const context = useContext(UserContext);
	if (context === null) {
		throw new Error("useUser deve ser usado dentro de um UserProvider");
	}
	return context;
}
